/**
 * Handing a file the server built to the browser. `MIG-005`.
 *
 * `POST /migration/exports` answers with the zip as base64 in the JSON body, because the write
 * queue carries JSON and `OFF-001a` puts every write through it. So the bytes arrive in memory and
 * something has to turn them into a saved file.
 *
 * **Injected into the screen rather than called from it**, for the reason the rest of this app
 * injects its transport: a download is the one thing a component test cannot observe. jsdom has no
 * `URL.createObjectURL` and clicking a synthetic anchor saves nothing, so a screen calling this
 * directly would have an untestable branch at the end of its most important flow. The screen takes
 * it as a prop, the test passes a spy and asserts the filename and the bytes, and this function is
 * the small piece that is exercised only in a browser.
 */
export function offerDownload(
  filename: string,
  base64: string,
  /**
   * The media type, because not every export is a zip: `CUS-006`'s per-customer file is JSON, and a
   * browser told it is a zip saves something nothing opens. Defaulted to the zip so `MIG-005`'s
   * caller is unchanged.
   */
  contentType = 'application/zip',
): void {
  // `atob` to bytes one char at a time: a base64 zip is binary, and `atob` gives a string whose
  // code units are the bytes. Reading them straight into a `Uint8Array` is what keeps the zip
  // valid, where `TextEncoder` would re-encode every byte above 0x7f as two.
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  // Revoked, or the blob is held for the life of the document: a shop exporting repeatedly would
  // keep every copy of its own database in memory.
  URL.revokeObjectURL(url)
}
